import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Chip, 
  Grid, 
  Stack
} from '@mui/material';
import { handleExportError } from "../../../utils/snackBarUtil";
import KpiCard from '../common/KpiCard';
import PieChart from '../common/PieChart';
import KpiWithPie from '../common/KpiWithPie';
import TrendChart from '../common/TrendChart';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import RowTypography from 'components/Table/RowTypography';
import LoaderComponent from 'components/Loader';
import { 
  fetchUserActivityTableauTableData,
  fetchTableauTotalLicenses,
  fetchTableauLicenseUtilization,
  fetchTableauLicenseUtilizationKpi,
  fetchTableauActivityTrendChart,
  fetchTableauExportActivityDetails,
  fetchTableauFilterOptions

} from '../../../service/service';
import { getPepwiseSectorList } from 'service/service';
import moment from 'moment';

interface User {
  id: string;
  userName: string;
  licenseType: string;
  lastLogin: string;
  sector: string;
  manager: string;
  status: 'Active' | 'Inactive' | 'At Risk';
  email?: string;
  gpid?: string;
  instance?: string;
  lastUpdated?: string;
}

interface TableauProps {
	filters?: {
		sector?: string | string[];
		filterOption?: string;
		startDate?: string;
		endDate?: string;
		filterList?: null | { [key: string]: any };
		chartTimePeriod?: string;
	};
	timeRange?: string;
	sectorsChange?: string;
	checkedListRef?: React.MutableRefObject<any>;
  resetFiltersRef?: React.MutableRefObject<(() => void) | null>;
}

interface LicenseTypeUtilization {
	active: number;
	inactive: number;
	assigned: number;
	total: number;
	utilizationPercentage: number;
}

interface LicenseUtilizationSector {
	sector: string;
	totalLicensedUsers: number;
	activeLicensedUsers: number;
	inactiveLicensedUsers: number;
	assignedLicensedUsers: number;
	utilizationPercentage: number;
	licenseTypeUtilization: Record<string, LicenseTypeUtilization>;
}

interface LicenseUtilizationKpiSector {
	sector: string;
	activeUsers: number;
	inactiveUsers: number;
  totalUsers: number;
  activeUserPercentage: number,
  licenseUtilizationPercentage: number,
  prevActiveUsers: number,
  prevTotalUsers: number,
  activeUserPercentageChange: number,
  activeUtilizationPct: number,
  prevActiveUtilizationPct: number,
  activeUtilizationChangePct: number
}

interface LicenseUtilizationKpi {
  lastRefresh: string,
  totalLicenseCount: number,
	totalUsers: number;
	totalActiveUsers: number;
  totalInactiveUsers: number;
  totalActiveUserPercentage: number;
	totalActiveUtilizationChangePct: number;
  totalLicenseUtilizationPercentage: number;
  totalLicenseUtilizationChangePct: number;
	sectorSummary: Record<string, LicenseUtilizationKpiSector>;
}

interface LicenseUtilizationSummary {
	totalLicensedUsers: number;
	totalActiveUsers: number;
	totalInactiveUsers: number;
	totalAssignedUsers: number;
	overallUtilizationPercentage: number;
	totalsByLicenseType: Record<string, number>;
	activeByLicenseType: Record<string, number>;
	inactiveByLicenseType: Record<string, number>;
	sectorBreakdown: LicenseUtilizationSector[];
}

type TimeRange = "year" | "LAST_30_DAYS" | "LAST_60_DAYS" | "LAST_90_DAYS" | "LAST_120_DAYS";

function getTimeRange(timeRange?: string): TimeRange {
      switch (timeRange) {
        case "year":
        case "LAST_30_DAYS":
        case "LAST_60_DAYS":
        case "LAST_90_DAYS":
        case "LAST_120_DAYS":
            return timeRange;
        default:
          return "LAST_30_DAYS"; // fallback
    }
}

const SECTOR_COLOR_PALETTE = [
	'#4A90E2',
	'#01C008',
	'#FFA500',
	'#9B59B6',
	'#2ECC71',
	'#E74C3C',
	'#1ABC9C',
	'#3498DB',
	'#F1C40F',
	'#7F8C8D',
	'#E67E22'
];

const UNUTILIZED_COLOR = '#95A5A6';

const KPI_TYPE_MAP: Record<string, string> = {
  ActiveUser: 'Active User',
  LicenseUtilization: 'License Utilization'
};

const LICENSE_TYPE_MAP: Record<string, string> = {
  Creators: 'Creator',
	Viewers: 'Viewer'
};

const licenseStatusColors = {
  'Active': { color: '#FFFFFF', bg: 'rgb(54, 168, 255)' },
  'Inactive': { color: '#FFFFFF', bg: '#FE2626'},
  'At Risk': { color: '#FFFFFF', bg: 'rgba(204, 130, 0, 0.7)' }
};

const userNameFormatter = ({ row }: { row: User }) => <RowTypography>{row.userName}</RowTypography>;
const licenseTypeFormatter = ({ row }: { row: User }) => <RowTypography>{row.licenseType}</RowTypography>;
const lastLoginFormatter = ({ row }: { row: User }) => <RowTypography>{row.lastLogin}</RowTypography>;
const sectorFormatter = ({ row }: { row: User }) => <RowTypography>{row.sector}</RowTypography>;
const managerFormatter = ({ row }: { row: User }) => <RowTypography>{row.manager}</RowTypography>;
const statusFormatter = ({ row }: { row: User }) => {
  const config = licenseStatusColors[row.status] || licenseStatusColors['Active'];
  return (
    <Chip
      label={row.status?.toUpperCase() || ""}
      size="medium"
      sx={{
        backgroundColor: `${config.bg} !important`,
        color: `${config.color} !important`,
        fontSize: '12px',
        borderRadius: '4px',
        height: '28px',
        minWidth: '116px',
        justifyContent: 'center',
        '& .MuiChip-label': {
          p: '4px 8px',
          fontWeight: 500,
          color: `${config.color} !important`,
          overflow: 'visible',
          textOverflow: 'clip',
          whiteSpace: 'nowrap'
        }
      }}
    />
  );
};

const TableauLicenses: React.FC<TableauProps>  = ({  timeRange, sectorsChange, checkedListRef, filters: externalFilters, resetFiltersRef })  => {
  const [activityTrendFilter, setActivityTrendFilter] = useState('All');
  const [totalLicensesFilter, setTotalLicensesFilter] = useState('All');
  const [userActivityTrendData, setUserActivityTrendData] = useState<any>({
    trendData: [],
    months: []
  });
  const [isTrendLoading, setIsTrendLoading] = useState<boolean>(false);

  const [tableData, setTableData] = useState([]);
  
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isTotalLicensesLoading, setIsTotalLicensesLoading] = useState(false);
  // keep the value slot to satisfy static analyzers while we only use the setter
  const [selectedTitle, setSelectedTitle] = useState<string>(KPI_TYPE_MAP['LicenseUtilization']);
  const [filterDataOptions, setFilterDataOptions] = useState<any>(null);
  

  const [totalLicensesData, setTotalLicensesData] = useState({
		number: 0,
		change: 0,
		changeType: 'increase' as const,
		pieData: [
			{ name: 'Active', value: 0 },
			{ name: 'Inactive', value: 0 },
			{ name: 'Unused', value: 0 }
		],
		colorMapping: {
			'Active': '#4A90E2',
			'Inactive': '#01C008',
			'Unused': '#FFA500'
		}
	});

  const [licenseUtilizationSummary, setLicenseUtilizationSummary] = useState<LicenseUtilizationSummary>({
    totalLicensedUsers: 0,
    totalActiveUsers: 0,
    totalInactiveUsers: 0,
    totalAssignedUsers: 0,
    overallUtilizationPercentage: 0,
    totalsByLicenseType: {},
    activeByLicenseType: {},
    inactiveByLicenseType: {},
    sectorBreakdown: []
  });


  const [licenseUtilizationKpi, setLicenseUtilizationKpi] = useState<LicenseUtilizationKpi>({
  lastRefresh: '',
  totalLicenseCount: 0,
  totalUsers: 0,
  totalActiveUsers: 0,
  totalInactiveUsers: 0,
  totalActiveUserPercentage: 0,
  totalActiveUtilizationChangePct: 0,
  totalLicenseUtilizationPercentage: 0,
  totalLicenseUtilizationChangePct: 0,
  sectorSummary: {}
});

  const [isLicenseUtilizationKpiLoading, setIsLicenseUtilizationKpiLoading] = useState(false);

  const [isLicenseUtilizationLoading, setIsLicenseUtilizationLoading] = useState(false);
  const [allSectors, setAllSectors] = useState<string[]>([]);
  const [areSectorsLoaded, setAreSectorsLoaded] = useState(false);

  useEffect(() => {
		if (resetFiltersRef) {
			resetFiltersRef.current = () => {
				setActivityTrendFilter('All');
				setTotalLicensesFilter('All');
			};
		}
		
		return () => {
			if (resetFiltersRef) {
				resetFiltersRef.current = null;
			}
		};
	}, [resetFiltersRef]);

  useEffect(() => {
    const loadSectors = async () => {
      try {
        const response = await getPepwiseSectorList();
        const sectorNames =
          response?.data?.sectors
            ?.map((sector: any) => sector?.Sector_Name)
            ?.filter((name: string) => !!name && name !== 'Unknown') || [];
        setAllSectors(sectorNames);
      } catch (error) {
        console.error('Error fetching sector list for Tableau licenses:', error);
      } finally {
        setAreSectorsLoaded(true);
      }
    };

    loadSectors();
  }, []);

  const getSectorPayload = useCallback(() => {
    let sectors: string;
    
    if (checkedListRef?.current && checkedListRef.current.length > 0) {
      sectors = checkedListRef.current.join(',');
    } else if (typeof externalFilters?.sector === 'string' && externalFilters.sector.toLowerCase() !== 'all') {
      sectors = externalFilters.sector;
    } else {
      const sector = externalFilters?.sector;
      if (Array.isArray(sector) && sector.length > 0) {
        sectors = sector.join(',');
      } else {
        sectors = allSectors.join(',');
      }
    }
    
    // Always append OTHER if it's not already included
    const sectorList = sectors.split(',').map(s => s.trim());
    if (!sectorList.some(s => s.toUpperCase() === 'OTHER')) {
      sectors = `${sectors},OTHER`;
    }
    
    return sectors;
  }, [checkedListRef, externalFilters?.sector, allSectors]);
  
  // Pagination and filtering - DYNAMIC FOR TABLE
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalElements: 0,
    pageSize: 100
  });
  
  const [filters, setFilters] = useState({
    sector: 'all',
    userStatusFilter: 'all',
    searchTerm: '',
    sortBy: 'lastLogin',
    orderBy: 'DESC'
  });

const [currentFilters, setCurrentFilters] = useState<Record<string, any>>({});

  // Fetch table data - ONLY DYNAMIC FUNCTION
  const fetchTableData = useCallback(async (page = 1, pageSize = 100) => {
     let sectorPayload = getSectorPayload();
      if (!sectorPayload) {
      return;
    }
    setIsTableLoading(true);
    console.info ('currentFilters', currentFilters)
    const hasSpecificSelection = checkedListRef?.current && checkedListRef.current.length > 0;
      const hasExternalSectorFilter = (typeof externalFilters?.sector === 'string' && externalFilters && externalFilters.sector.toLowerCase() !== 'all') || (Array.isArray(externalFilters?.sector) && externalFilters && externalFilters.sector.length > 0);

      if (!hasSpecificSelection && !hasExternalSectorFilter) {
        sectorPayload = `${sectorPayload},OTHER`;
      }
    try {
      const params = {
        page,
        pageSize,
        sector: sectorPayload,
        userStatusFilter: filters.userStatusFilter,
        searchTerm: filters.searchTerm,
        sortBy: filters.sortBy || "lastLogin",
        orderBy: filters.orderBy,
        filterBy: currentFilters,
		filterOption:timeRange
      };

      const response = await fetchUserActivityTableauTableData(params);
      
      if (response?.success) {
        setTableData(response.data.users || []);
        setPagination(response.data.pagination);
      } else {
        // Fallback to empty data if API fails
        setTableData([]);
        setPagination({
          currentPage: 1,
          totalPages: 0,
          totalElements: 0,
          pageSize: pageSize
        });
      }
    } catch (error) {
      console.error('Error fetching table data:', error);
      // Fallback to empty data
      setTableData([]);
      setPagination({
        currentPage: 1,
        totalPages: 0,
        totalElements: 0,
        pageSize: pageSize
      });
    } finally {
      setIsTableLoading(false);
    }
  }, [filters, currentFilters,timeRange,getSectorPayload,]);

  const fetchTotalLicenses = useCallback(async () => {
    setIsTotalLicensesLoading(true);
    try {
      const payload = timeRange ? { filterOption: timeRange, role: totalLicensesFilter } : {};
      const response = await fetchTableauTotalLicenses(payload);
      
      if (response?.success && response?.data) {
        const data = response.data;
        setTotalLicensesData({
          number: data.number || 0,
          change: data.change || 0,
          changeType: data.changeType || 'increase',
          pieData: data.pieData || [],
          colorMapping: data.colorMapping || {
            'Active': '#4A90E2',
            'Inactive': '#01C008',
            'Unused': '#FFA500'
          }
        });
      }
    } catch (error) {
      console.error('Error fetching total licenses data:', error);
    } finally {
      setIsTotalLicensesLoading(false);
    }
  }, [getSectorPayload, timeRange, totalLicensesFilter]);

  const fetchUserActivityTrend = useCallback(async () => {
    const sectorPayload =  getSectorPayload();

    if (!sectorPayload) {
      return;
    }

    try {
      setIsTrendLoading(true);
      const payload = { 
        sector: sectorPayload, 
        role: activityTrendFilter
      };
      const response = await fetchTableauActivityTrendChart(payload);
      
      console.log("responseDATA", response);
      
      // Store the API response data directly
      if (response?.success && response?.data) {
        setUserActivityTrendData(response.data);
      } else {
        // Set empty data if response is not successful
        setUserActivityTrendData({
          trendData: [],
          months: []
        });
      }
    } catch (error) {
      console.error('Error fetching Activity Trend data:', error);
      setUserActivityTrendData({
        trendData: [],
        months: []
      });
    } finally {
      setIsTrendLoading(false);
    }
  }, [getSectorPayload, activityTrendFilter]);



   function resetLicenseUtilizationKpi() {
        setLicenseUtilizationKpi({
          lastRefresh: '',
          totalLicenseCount: 0,
          totalActiveUsers: 0,
          totalUsers: 0,
          totalInactiveUsers: 0,
          totalActiveUserPercentage: 0,
          totalActiveUtilizationChangePct: 0,
          totalLicenseUtilizationPercentage: 0,
          totalLicenseUtilizationChangePct: 0,
          sectorSummary: {}
        });}

const fetchLicenseUtilizationKpi = useCallback(async () => {
    setIsLicenseUtilizationKpiLoading(true);
    try {
      let sectorPayload = getSectorPayload();

      if (!sectorPayload) {
        resetLicenseUtilizationKpi()
        return;
      }

      const hasSpecificSelection = checkedListRef?.current && checkedListRef.current.length > 0;
      const hasExternalSectorFilter = (typeof externalFilters?.sector === 'string' && externalFilters && externalFilters.sector.toLowerCase() !== 'all') || (Array.isArray(externalFilters?.sector) && externalFilters && externalFilters.sector.length > 0);

      // Add "OTHER" only when showing all sectors (no specific selection)
      if (!hasSpecificSelection && !hasExternalSectorFilter) {
        sectorPayload = `${sectorPayload},OTHER`;
      }

      const payload = {
        sector: sectorPayload,
        filterOption: timeRange || externalFilters?.filterOption || 'LAST_30_DAYS'
      };

      const response = await fetchTableauLicenseUtilizationKpi(payload);

      if (response?.success && response?.data?.utilizationStats) {
        setLicenseUtilizationKpi({
          lastRefresh: response.data.utilizationStats.lastRefresh || '',
          totalLicenseCount:response.data.utilizationStats.utilizationSummary.totalLicenseCount,
          totalActiveUsers: response.data.utilizationStats.utilizationSummary.totalActiveUsers,
          totalInactiveUsers: response.data.utilizationStats.utilizationSummary.totalInactiveUsers,
          totalUsers: response.data.utilizationStats.utilizationSummary.totalUsers,
          totalActiveUserPercentage: response.data.utilizationStats.utilizationSummary.totalActiveUserPercentage,
          totalActiveUtilizationChangePct: response.data.utilizationStats.utilizationSummary.totalActiveUtilizationChangePct,
          totalLicenseUtilizationPercentage: response.data.utilizationStats.utilizationSummary.totalLicenseUtilizationPercentage,
          totalLicenseUtilizationChangePct: response.data.utilizationStats.utilizationSummary.totalLicenseUtilizationChangePct,
          sectorSummary: response.data.utilizationStats.sectorSummary || {}
        });
      } else {
        resetLicenseUtilizationKpi()
      }
    } catch (error) {
      console.error('Error fetching license utilization Kpi data:', error);
      resetLicenseUtilizationKpi();
    } finally {
      setIsLicenseUtilizationKpiLoading(false);
    }
  }, [getSectorPayload, timeRange, externalFilters?.filterOption]);

  const fetchLicenseUtilizationBySector = useCallback(async () => {
    setIsLicenseUtilizationLoading(true);
    try {
      let sectorPayload = getSectorPayload();

      if (!sectorPayload) {
        setLicenseUtilizationSummary({
          totalLicensedUsers: 0,
          totalActiveUsers: 0,
          totalInactiveUsers: 0,
          totalAssignedUsers: 0,
          overallUtilizationPercentage: 0,
          totalsByLicenseType: {},
          activeByLicenseType: {},
          inactiveByLicenseType: {},
          sectorBreakdown: []
        });
        return;
      }

      // Check if user has selected specific sectors or showing all
      const hasSpecificSelection = checkedListRef?.current && checkedListRef.current.length > 0;
      const hasExternalSectorFilter = (typeof externalFilters?.sector === 'string' && externalFilters && externalFilters.sector.toLowerCase() !== 'all') || (Array.isArray(externalFilters?.sector) && externalFilters && externalFilters.sector.length > 0);


      // Add "OTHER" only when showing all sectors (no specific selection)
      if (!hasSpecificSelection && !hasExternalSectorFilter) {
        sectorPayload = `${sectorPayload},OTHER`;
      }
      
      const payload = {
        sector: sectorPayload,
        filterOption: timeRange || externalFilters?.filterOption || 'LAST_30_DAYS'
      };

      const response = await fetchTableauLicenseUtilization(payload);

      if (response?.success && response?.data) {
        setLicenseUtilizationSummary({
          totalLicensedUsers: response.data.totalLicensedUsers || 0,
          totalActiveUsers: response.data.totalActiveUsers || 0,
          totalInactiveUsers: response.data.totalInactiveUsers || 0,
          totalAssignedUsers: response.data.totalAssignedUsers || 0,
          overallUtilizationPercentage: response.data.overallUtilizationPercentage || 0,
          totalsByLicenseType: response.data.totalsByLicenseType || {},
          activeByLicenseType: response.data.activeByLicenseType || {},
          inactiveByLicenseType: response.data.inactiveByLicenseType || {},
          sectorBreakdown: response.data.sectorBreakdown || []
        });
      } else {
        setLicenseUtilizationSummary({
          totalLicensedUsers: 0,
          totalActiveUsers: 0,
          totalInactiveUsers: 0,
          totalAssignedUsers: 0,
          overallUtilizationPercentage: 0,
          totalsByLicenseType: {},
          activeByLicenseType: {},
          inactiveByLicenseType: {},
          sectorBreakdown: []
        });
      }
    } catch (error) {
      console.error('Error fetching license utilization data:', error);
      setLicenseUtilizationSummary({
        totalLicensedUsers: 0,
        totalActiveUsers: 0,
        totalInactiveUsers: 0,
        totalAssignedUsers: 0,
        overallUtilizationPercentage: 0,
        totalsByLicenseType: {},
        activeByLicenseType: {},
        inactiveByLicenseType: {},
        sectorBreakdown: []
      });
    } finally {
      setIsLicenseUtilizationLoading(false);
    }
  }, [getSectorPayload, timeRange, externalFilters?.filterOption, externalFilters?.sector, checkedListRef]);


  useEffect(() => {
    fetchTableData();
  }, [fetchTableData]);

  useEffect(() => {
    if (!areSectorsLoaded) {
      return;
    }
    fetchUserActivityTrend();
  }, [fetchUserActivityTrend, activityTrendFilter, sectorsChange, areSectorsLoaded]);

  useEffect(() => {
    if (!areSectorsLoaded) {
      return;
    }
    fetchTotalLicenses();
  }, [fetchTotalLicenses, totalLicensesFilter, timeRange, areSectorsLoaded]);

  useEffect(() => {
    if (!areSectorsLoaded) {
      return;
    }
    fetchLicenseUtilizationBySector();
  }, [fetchLicenseUtilizationBySector, sectorsChange, areSectorsLoaded]);

  useEffect(() => {
    if (!areSectorsLoaded) {
      return;
    }
    fetchLicenseUtilizationKpi();
  }, [fetchLicenseUtilizationKpi, sectorsChange, areSectorsLoaded]);

  useEffect(() => {
    fetchTableData(1, pagination.pageSize);
  }, [filters, currentFilters, pagination.pageSize, fetchTableData]);

  const handleSearch = async (searchDetails: any, columnKey: any) => {
  console.log('searchdetailss', searchDetails, columnKey);

  if (searchDetails?.length >= 3) {
    try {
      let response;

      if (columnKey === 'userName') {
        response = await fetchTableauFilterOptions(columnKey, searchDetails);
      }
      else if(columnKey === "manager") {
         response = await fetchTableauFilterOptions("mgrName", searchDetails);
      }
      else {
        return;
      }

      const options = response?.data || [];
      setFilterDataOptions((prev: any) => ({
        ...prev,
        [columnKey]: options
      }));
    } catch (error) {
      console.error('Error performing search:', error);
    }

  } else if (searchDetails?.length === 0) {
    if (columnKey === 'userName') {
      const response = await fetchTableauFilterOptions(columnKey, '');
      const initialOptions = response?.data || [];
      setFilterDataOptions((prev: any) => ({
        ...prev,
        [columnKey]: initialOptions
      }));
    } else if (columnKey === 'manager'){
      const response = await fetchTableauFilterOptions('mgrName', '');
      const initialOptions = response?.data || [];
      setFilterDataOptions((prev: any) => ({
        ...prev,
        [columnKey]: initialOptions
      }));
    }
  }
};

const handleFilterChange = (filter: { [key: string]: any }) => {
  const filterListMap: { [key: string]: any } = {};
  const clearedColumns: string[] = [];

  Object.keys(filter).forEach((key) => {
    const value = filter[key];

    if (value == null || (Array.isArray(value) && value.length === 0)) {
      clearedColumns.push(key);
      return;
    }

    if (key === "lastLogin" && Array.isArray(value) && value.length === 2) {
      const [startMoment, endMoment] = value;

      let utcStart = startMoment;
      let utcEnd = endMoment;

      if (moment.isMoment(startMoment)) {
        utcStart = moment.utc([
          startMoment.year(),
          startMoment.month(),
          startMoment.date(),
          startMoment.hours(),
          startMoment.minutes(),
          startMoment.seconds()
        ]).toISOString();
      }

      if (moment.isMoment(endMoment)) {
        utcEnd = moment.utc([
          endMoment.year(),
          endMoment.month(),
          endMoment.date(),
          endMoment.hours(),
          endMoment.minutes(),
          endMoment.seconds()
        ]).toISOString();
      }

      filterListMap[key] = [utcStart, utcEnd];
    } else {
      filterListMap[key] = value;
    }
  });

 setFilters((prev: any) => {
  const newFilterInfo = { ...prev, ...filterListMap };
            clearedColumns.forEach(col => {
        delete newFilterInfo[col];
      });
      
      return newFilterInfo;
});
  setCurrentFilters((prev) => {
    const updatedFilters = {
      ...prev,
      ...filterListMap
    };

    clearedColumns.forEach((col) => {
      delete updatedFilters[col];
    });

    return updatedFilters;
  });


  setPagination((prev: any) => ({
    ...prev,
    currentPage: 1
  }));

  if (clearedColumns.length > 0) {
    clearedColumns.forEach(async (columnKey) => {
      try {
        let response;

        if (columnKey === "userName") {
          response = await fetchTableauFilterOptions(columnKey, "");
        } else if (columnKey === "manager") {
          response = await fetchTableauFilterOptions("mgrName", "");
        }  else {
          return;
        }

        const options = response?.data || [];

        setFilterDataOptions((prev: any) => ({
          ...prev,
          [columnKey]: options
        }));
      } catch (error) {
        console.error(
          `Error refreshing filter options for ${columnKey}:`,
          error
        );
      }
    });
  }
};


  const handleSorting = useCallback((sorting: any) => {
    setFilters(prev => ({
      ...prev,
      sortBy: sorting.sortBy,
      orderBy: sorting.sortDir
    }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, []);

  const handlePageChange = useCallback((event: any, value: any) => {
    setPagination(prev => ({ ...prev, currentPage: value }));
    fetchTableData(value, pagination.pageSize);
  }, [pagination.pageSize, fetchTableData]);

  const handlePageSizeChange = useCallback((event: any) => {
    const newSize = Number.parseInt(event.target.value);
    setPagination(prev => ({ ...prev, pageSize: newSize, currentPage: 1 }));
    fetchTableData(1, newSize);
  }, [fetchTableData]);

  const handleExport = useCallback(async () => {
    try {
      const params = {
        ...filters,
        filterBy: currentFilters,
        formatType: "EXCEL",
        tableName: "tableau-licenses",
        exportColumns: defaultLicenseColumns
      };
      
      
    const cleanFilters = Object.fromEntries(
      Object.entries(currentFilters).filter(([_, v]) => Array.isArray(v) ? v.length > 0 : v != null)
    );

    const selectedSectors = checkedListRef?.current?.length > 0 
      ? checkedListRef?.current?.join(',') 
      : allSectors.join(',');

      const requestBody = {
      ...filters,
       filterOption: timeRange || externalFilters?.filterOption || 'LAST_30_DAYS',
      sector: selectedSectors,
      filterBy: cleanFilters,
      exportColumns: defaultLicenseColumns,
      formatType: "EXCEL",
      tableName: "tableau-licenses",
    };

    console.log('Exporting data with params:', params);

    let res = await fetchTableauExportActivityDetails(requestBody);
    console.log(res)
      
  } catch (error) {
      console.error('Error exporting Tableau License data:', error);
      handleExportError(error);
    }
  }, [filters, currentFilters]);

  const licenseUsersColumns = useMemo(() => [
     {
      key: 'userName',
      name: 'User Name',
      minWidth: 150,
      formatter: userNameFormatter,
      resizable: true,
      flexGrow: 1
    },
    {
      key: 'licenseType',
      name: 'License Type',
      minWidth: 120,
      formatter: licenseTypeFormatter,
      resizable: true,
      flexGrow: 1
    },
    {
      key: 'lastLogin',
      name: 'Last Login',
      minWidth: 150,
      formatter: lastLoginFormatter,
      resizable: true,
      flexGrow: 1
    },
    {
      key: 'sector',
      name: 'Sector',
      minWidth: 120,
      formatter: sectorFormatter,
      resizable: true,
      flexGrow: 1
    },
    {
      key: 'manager',
      name: 'Manager',
      minWidth: 140,
      formatter: managerFormatter,
      resizable: true,
      flexGrow: 1
    },
    {
      key: 'status',
      name: 'Status',
      minWidth: 140,
      formatter: statusFormatter,
      resizable: true,
      flexGrow: 1
    }
  ], []);

  const defaultLicenseColumns = [
    'User Name',
    'License Type',
    'Last Login',
    'Sector',
    'Manager',
    'Status'
  ];



  // Updated pie data calculation with utilization percentages
  const licenseUtilizationPieData = useMemo(() => {
    const breakdown = licenseUtilizationSummary?.sectorBreakdown || [];
    const grandTotal = licenseUtilizationSummary?.totalLicensedUsers || 0;
    
    if (grandTotal === 0) {
      return [];
    }

    const pieData: Array<{ name: string; value: number; isUtilized?: boolean; count?: number }> = [];
    let totalAssignedPercentage = 0;

    // Calculate each sector's percentage relative to the GRAND TOTAL
    breakdown.forEach((sector) => {
      const assignedInSector = sector.assignedLicensedUsers; // active + inactive
      
      // Calculate percentage as: (assigned in this sector / total across ALL selected sectors) * 100
      const sectorPercentage = (assignedInSector / grandTotal) * 100;

      if (assignedInSector > 0) {
        pieData.push({
          name: sector.sector,
          value: Number.parseFloat(sectorPercentage.toFixed(2)),
          count: assignedInSector,
          isUtilized: true
        });
      }

      // Accumulate total assigned percentage
      totalAssignedPercentage += sectorPercentage;
    });

    // Calculate unutilized percentage (100 - total assigned)
    const unutilizedPercentage = 100 - totalAssignedPercentage;
    const unutilizedCount = licenseUtilizationSummary?.totalLicensedUsers - licenseUtilizationSummary?.totalActiveUsers;

    // Add single unutilized entry
    if (unutilizedPercentage > 0 && unutilizedCount > 0) {
      pieData.push({
        name: 'Unused',
        value: Number.parseFloat(unutilizedPercentage.toFixed(2)),
        count: unutilizedCount,
        isUtilized: false
      });
    }

    return pieData;
  }, [licenseUtilizationSummary]);

  
  const licenseUtilizationPercentage = useMemo(() => {
    if (!licenseUtilizationKpi?.totalLicenseUtilizationPercentage) return 0;

    return Number(licenseUtilizationKpi?.totalLicenseUtilizationPercentage.toFixed(2)); // round to 2 decimals
  }, [licenseUtilizationKpi]);

  const handleKPICardClick = useCallback((cardTitle: string) => {
      console.log('KPI Card clicked:', cardTitle);
      
      const newSelectedTitle = selectedTitle === cardTitle ? '' : cardTitle;
      setSelectedTitle(newSelectedTitle);
      if (newSelectedTitle === '') {
        setCurrentFilters({});
      } else {
        let newFilterInfo: { [key: string]: any } = {};
        switch (newSelectedTitle) {
          case KPI_TYPE_MAP.ActiveUser:
            newFilterInfo = { status: ['Active'] };
            break;
          case KPI_TYPE_MAP.LicenseUtilization:
            newFilterInfo = { status: ['Active', 'Inactive'] };
            break;
          default:
            newFilterInfo = {};
        }
        setCurrentFilters(newFilterInfo);
      }
}, [selectedTitle]); 

  const getNumberColor = (num: number, kpiType: string) => {
    if (kpiType === KPI_TYPE_MAP.ActiveUser)
    {
      if (num > 75) return 'success.main';
      if (num >= 50) return 'warning.main';
      return 'error.main';
    }
    else if (kpiType === KPI_TYPE_MAP.LicenseUtilization)
    {
      if (num > 95) return 'error.main';
      if (num >= 80) return 'warning.main';
      return 'success.main';
    }
};

  const fetchFilterOptions = async () => {
    try {
      let sectors = await getPepwiseSectorList();
      const filterSectors = sectors?.data?.sectors.map((s: any) => s.Sector_Name) || [];

      const usersResponse = await fetchTableauFilterOptions('userName');
      const users = usersResponse?.data || [];

      const managerResponse = await fetchTableauFilterOptions('mgrName');
      const manager = managerResponse?.data || [];

      setFilterDataOptions({
        sector: filterSectors,
        licenseType: ['Creator', 'Viewer','Explorer'],
        userName: users,
        status: ['Active', 'Inactive'],
        manager:manager
      });
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

     useEffect(() => {
            fetchFilterOptions();
        }, []);

    useEffect(() => {
    let licenseFilter: string[] | null = null;

    switch (activityTrendFilter) {
      case "Creators":
        licenseFilter = ["Creator"];
        break;
      case "Viewers":
        licenseFilter = ["Viewer"];
        break;
      case "Explorers":
        licenseFilter = ["Explorer"];
        break;
      default:
        licenseFilter = null; 
    }

    setCurrentFilters(prev => {
      const updated = { ...prev };

      if (licenseFilter) {
        updated.licenseType = licenseFilter;
      } else {
        delete updated.licenseType;
      }
      return updated;
    });

  }, [activityTrendFilter]);


const activeUserPercentage = useMemo(() => {
    if (!licenseUtilizationKpi?.totalActiveUserPercentage) return 0;
    return Number(licenseUtilizationKpi?.totalActiveUserPercentage.toFixed(2)); // round to 2 decimals
  }, [licenseUtilizationKpi]);
  
  const activeUserHoverText = useMemo(() => {
    if (!licenseUtilizationKpi?.totalUsers) return "";

    return `${licenseUtilizationKpi.totalActiveUsers} of ${licenseUtilizationKpi.totalUsers}`;
          
    }, [licenseUtilizationKpi]);

  const activeUserPercentageChange = useMemo(() => {
    if (!licenseUtilizationKpi?.totalActiveUtilizationChangePct) return 0;
 
    return Number(licenseUtilizationKpi?.totalActiveUtilizationChangePct.toFixed(2)); // round to 2 decimals
  }, [licenseUtilizationKpi]);

  const licenseUtilizationHoverText = useMemo(() => {
    if (!licenseUtilizationKpi?.totalLicenseUtilizationPercentage) return "";

    return `${licenseUtilizationKpi.totalUsers} of ${licenseUtilizationKpi.totalLicenseCount}`;
          
    }, [licenseUtilizationKpi]);

  const licenseUtilizationPercentageChange = useMemo(() => {
    if (!licenseUtilizationKpi?.totalLicenseUtilizationChangePct) return 0;
 
    return Number(licenseUtilizationKpi?.totalLicenseUtilizationChangePct.toFixed(2)); // round to 2 decimals
  }, [licenseUtilizationKpi]);
  
  const licenseLastRefresh = useMemo(() => {
    if (!licenseUtilizationKpi?.lastRefresh) return "";
 
    return licenseUtilizationKpi?.lastRefresh; // round to 2 decimals
  }, [licenseUtilizationKpi]);
  
  // Updated color mapping to handle utilized/unutilized
  const sectorColorMapping = useMemo(() => {
    const breakdown = licenseUtilizationSummary?.sectorBreakdown || [];
    if (!breakdown.length) {
      return {};
    }

    const mapping: Record<string, string> = {};
    
    breakdown.forEach((sector, index) => {
      // Assign color to utilized portion (sector name only)
      mapping[sector.sector] = SECTOR_COLOR_PALETTE[index % SECTOR_COLOR_PALETTE.length];
    });
    
    // Assign gray to single unutilized entry
    mapping['Unused'] = UNUTILIZED_COLOR;

    return mapping;
  }, [licenseUtilizationSummary]);

  const formatDate = (date: Date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' }); // Dec, Jan, Feb...
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
 };

 
 	return (
		<Box sx={{ width: '100%' }}>
			{/* First Row - Total Licenses, License Utilization by Sectors, Expiring Licenses */}
			<Grid container spacing={2} sx={{ mb: 2 }}>
				{/* Total Licenses with Pie Chart */}
				<Grid item xs={12} md={4}>
					<KpiWithPie
						isLoading={isTotalLicensesLoading}
						title="Total Licenses"
						number={totalLicensesData.number}
						pieData={totalLicensesData.pieData}
						colorMapping={totalLicensesData.colorMapping}
            hasTooltip={true}
            tooltipText="Total number of Explorer, Creator, and Viewer Tableau licenses. Calendar and Sector filters are not applicable."
            hasDropdown={true}
						dropdownValue={totalLicensesFilter}
						dropdownOptions={[
              { label: 'All', value: 'All' },
							{ label: 'Creator', value: 'Creators' },
							{ label: 'Viewer', value: 'Viewers' },
							{ label: 'Explorer', value: 'Explorers' }
						]}
						onDropdownChange={(value) => setTotalLicensesFilter(value)}
						containerProps={{
							sx: {
								minHeight: '280px',
							}
						}}
					/>
				</Grid>

				{/* License Utilization by Sectors */}
				<Grid item xs={12} md={4}>
					<PieChart
						isLoading={isLicenseUtilizationLoading}
						title="License Utilization by Sectors"
						data={licenseUtilizationPieData}
						colorMapping={sectorColorMapping}
						showUtilizationPercentage={true}
            hasTooltip={true}
            tooltipText="Percentage of Tableau licenses assigned to users in each sector"
						isTextTooltip={true}
            containerProps={{
							sx: {
								minHeight: '280px',
                width: '100%',
							}
						}}
					/>

				</Grid>

				{/* Expiring Licenses */}
				<Grid item xs={12} md={4}>
					<KpiCard
						isLoading={false}
						title="Expiry Date"
            hasTooltip={true}
            theme="light"
            isTextTooltip={true}
            tooltipLabel={"Expiry date of purchased licenses"}
            number={formatDate (new Date(2027, 1, 7))}
						containerProps={{
							sx: {
								minHeight: '280px',
							}
						}}
					/>
				</Grid>
			</Grid>

			{/* Second Row - License Usage Trend and Users Activity Trends */}
      <Grid container spacing={2} sx={{ mb: 2, alignItems: 'stretch' }}>
        <Grid item xs={12} md={4}>
          <Stack spacing={2} sx={{ display: 'flex', direction: 'column'}}>
            <Box sx={{ flex: 1 }}>
              <KpiCard
                isLoading={isLicenseUtilizationKpiLoading}
                title="License Utilization %"
                theme="light"
                isTextTooltip={true}
                isSelected = {selectedTitle === KPI_TYPE_MAP['LicenseUtilization']}
                comparePeriod = 'LAST_30_DAYS'
                reverseTrendColor = {true}
                number={licenseUtilizationPercentage}
                change={licenseUtilizationPercentageChange}
                changeType={licenseUtilizationPercentageChange > 0 ? 'increase' : 'decrease'}
                showChangePercentage={false}
                units="%"
                hasTooltip={true}
                tooltipLabel={licenseLastRefresh ? `Last Data Refresh (UTC): ${licenseLastRefresh}` : ''}
                hoverText={licenseUtilizationHoverText}
                numberColor={
                  getNumberColor(licenseUtilizationPercentage, KPI_TYPE_MAP['LicenseUtilization'])
                }
                containerProps={{
                  sx: {
                    height: '100%',
                    minHeight: '192px',
                  },
                  onClick: () => handleKPICardClick(KPI_TYPE_MAP['LicenseUtilization'])
                }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <KpiCard
                isLoading={isLicenseUtilizationKpiLoading}
                title="Active User %"
                theme="light"
                isTextTooltip={true}
                comparePeriod = 'LAST_30_DAYS'
                isSelected={selectedTitle === KPI_TYPE_MAP['ActiveUser']}
                reverseTrendColor = {false}
                number={activeUserPercentage}
                change={activeUserPercentageChange}
                changeType={activeUserPercentageChange > 0 ? 'increase' : 'decrease'}
                showChangePercentage={false}
                hasTooltip={true}
                tooltipLabel={"Percentage of users having last login time within the selected date range"}
                hoverText={activeUserHoverText}
                units="%"
                numberColor={
                  getNumberColor(activeUserPercentage, KPI_TYPE_MAP['ActiveUser'])}
                containerProps={{
                  sx: {
                    height: '100%',
                    minHeight: '192px',
                  },
                  onClick: () => handleKPICardClick(KPI_TYPE_MAP['ActiveUser'])
                }}
              />
            </Box>
          </Stack>
        </Grid>

        {/* Users Activity Trends */}
        <Grid item xs={12} md={8}>
          <TrendChart
            isLoading={isTrendLoading}
            title="Users Activity Trends"
            data={userActivityTrendData}
            hasDropdown={true}
            dropdownValue={activityTrendFilter}
            dropdownOptions={[
              { label: 'All', value: 'All' },
              { label: 'Creator', value: 'Creators' },
              { label: 'Viewer', value: 'Viewers' },
              { label: 'Explorer', value: 'Explorers' }
            ]}
            onDropdownChange={(value) => setActivityTrendFilter(value)}
            activeLabel="Active Users"
            inactiveLabel="Inactive Users"
            activeColor="#4A90E2"
            inactiveColor="#ff4444"
            containerProps={{
              sx: {
                minHeight: '400px',
                height: '100%'
              }
            }}
          />
        </Grid>
      </Grid>

			 <Box mt={2}>
        {isTableLoading && tableData.length === 0 ? (
          <Stack justifyContent="center" alignItems="center" minHeight="200px">
            <LoaderComponent />
          </Stack>
        ) : (
          <CustomizedDataGrid
            title={`User Activity Monitoring${selectedTitle ? ' - ' + selectedTitle : ''}`}
            tableColumns={licenseUsersColumns}
            tableData={tableData}
            tableType="tableau-license-users"
            defaultColumnNames={defaultLicenseColumns}
            onSorting={handleSorting}
            currSorting={{
              sortBy: filters.sortBy,
              sortDir: filters.orderBy
            }}
            filterDataOptions={filterDataOptions}
            searchDataValues={{}}
            onFilterCheck={handleFilterChange}
            currFilter={currentFilters}
            onFilterSearch={handleSearch}
            onHeaderSelectAll={() => {}}
            onFilterClick={() => {}}
            showExport={true}
            fileName="tableau-licenses-report"
            tableName="tableau-licenses"
            formatType="EXCEL"
            exportColumns={defaultLicenseColumns}
            exportTableData={handleExport}
            page={pagination.currentPage}
            pageSize={pagination.pageSize}
            paginationData={pagination}
            listLength={tableData.length}
            handlepageSizeChange={handlePageSizeChange}
            handlePageChange={handlePageChange}
            isFetching={isTableLoading}
            isDateRangeFilter={true}
            isDisabled={false}
            // isShowMoreButton={false}
            pageSizeOptions={[100, 200, 500, 1000]}
          />
        )}
      </Box>
		</Box>
	);
};

export default TableauLicenses;